/**
 * Retry mechanism depending on the error.
 * @example
 * ```ts
 * const sync = retryWhen(LiveUpdates.sync, {
 *   shouldRetry: (error: unknown) => error instanceof Error && error.message.includes("unable to reach host"),
 *   maxRetries: 5,
 *   retryDelayInMs: 500, // retry after 500ms.
 * })
 * ```
 */
export function retryWhen<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  options: {
    shouldRetry: (error: unknown) => boolean;
    maxRetries: number;
    retryDelayInMs?: number;
  }
): (...args: Args) => Promise<T> {
  const { shouldRetry, maxRetries, retryDelayInMs = 0 } = options;
  return (...args) =>
    fn(...args).catch(async (error) => {
      if (maxRetries <= 0) throw error;
      if (!shouldRetry(error)) throw error;
      const retry = retryWhen(fn, { shouldRetry, maxRetries: maxRetries - 1 });
      await new Promise((resolve) => setTimeout(resolve, retryDelayInMs));
      return retry(...args);
    });
}
